import type { GameState, Player, PointState, LegalTarget, ReinePickup } from './types';

export function createInitialState(): GameState {
  const points: PointState[] = Array.from({ length: 25 }, () => ({
    topOwner: null,
    topCount: 0,
    pinnedOwner: null,
  }));

  // All white start at point 24, all black at point 1
  points[24] = { topOwner: 'white', topCount: 15, pinnedOwner: null };
  points[1]  = { topOwner: 'black', topCount: 15, pinnedOwner: null };

  return {
    points,
    borneOff: { white: 0, black: 0 },
    dice: [],
    diceUsed: [],
    currentPlayer: 'white',
    phase: 'rolling',
    isReine: false,
    reinePickup: null,
    winner: null,
    marsCheck: false,
    hasMoved: { white: false, black: false },
    selection: null,
    legalTargets: [],
    scores: { white: 0, black: 0 },
    motherOutcome: null,
  };
}

export function cloneState(s: GameState): GameState {
  return {
    ...s,
    points: s.points.map(p => ({ ...p })),
    borneOff: { ...s.borneOff },
    dice: [...s.dice],
    diceUsed: [...s.diceUsed],
    hasMoved: { ...s.hasMoved },
    legalTargets: s.legalTargets.map(t => ({ ...t, dieIndices: [...t.dieIndices] })),
    scores: { ...s.scores },
  };
}

// Remove a checker from a point (top stack, or pinned piece).
// Returns the mutated clone.
export function liftPiece(s: GameState, point: number, wasPinned: boolean): GameState {
  const st = cloneState(s);
  const pt = st.points[point]!;
  if (wasPinned) {
    pt.pinnedOwner = null;
  } else {
    pt.topCount -= 1;
    if (pt.topCount === 0) {
      // If there was a pinned piece underneath, it is now free (becomes top)
      if (pt.pinnedOwner !== null) {
        pt.topOwner = pt.pinnedOwner;
        pt.topCount = 1;
        pt.pinnedOwner = null;
      } else {
        pt.topOwner = null;
      }
    }
  }
  return st;
}

// Place a checker of `owner` onto `dest` point (or bear off if dest === 0).
export function placePiece(s: GameState, owner: Player, dest: number): GameState {
  const st = cloneState(s);
  if (dest === 0) {
    st.borneOff[owner] += 1;
    return st;
  }
  const pt = st.points[dest]!;
  if (pt.topOwner === null) {
    pt.topOwner = owner;
    pt.topCount = 1;
  } else if (pt.topOwner === owner) {
    pt.topCount += 1;
  } else {
    // Pinning an enemy lone checker
    pt.pinnedOwner = pt.topOwner;
    pt.topOwner = owner;
    pt.topCount = 1;
  }
  return st;
}

// Apply a normal (dice-based) move. Consumes the die at dieIndex.
export function applyMove(
  s: GameState,
  from: number,
  to: number,   // 0 = bear off
  dieIndex: number,
): GameState {
  let st = liftPiece(s, from, false);
  st = placePiece(st, s.currentPlayer, to);
  st.diceUsed[dieIndex] = true;
  if (!st.hasMoved[s.currentPlayer]) st.hasMoved[s.currentPlayer] = true;
  st.selection = null;
  st.legalTargets = [];
  return st;
}

// Apply a reine (free) move. Does not consume dice; ends turn immediately.
// NOTE: the piece is already absent from the board — it was lifted during the
// pickup phase in main.ts.  Only the placement step is needed here.
export function applyReineMove(
  s: GameState,
  pickup: ReinePickup,
  to: number,
): GameState {
  const st = placePiece(s, pickup.owner, to);
  st.reinePickup = null;
  st.legalTargets = [];
  return st;
}

// Undo a reine pickup — return the piece to its original slot without disturbing
// the rest of the board.  Used when the player cancels before placing.
export function undoReinePickup(s: GameState, pickup: ReinePickup): GameState {
  if (pickup.wasPinned) {
    // The lifted piece was the pinnedOwner; just restore that field directly.
    const st = cloneState(s);
    st.points[pickup.point]!.pinnedOwner = pickup.owner;
    return st;
  } else {
    // The lifted piece was the top (or one of the top stack).
    // placePiece handles all sub-cases: empty point, own stack, or re-pinning the freed piece.
    return placePiece(s, pickup.owner, pickup.point);
  }
}

export function opponent(p: Player): Player {
  return p === 'white' ? 'black' : 'white';
}

// Points that belong to the white home board (1–6) and black home board (19–24)
export const HOME_RANGE: Record<Player, [number, number]> = {
  white: [1, 6],
  black: [19, 24],
};

// Starting point for each player (where their mother lives)
export const START_POINT: Record<Player, number> = {
  white: 24,
  black: 1,
};
